import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const LABELS = { highest_coaching_qualification: "Qualification", dbs_held: "DBS", dbs_expiry: "DBS expiry", safeguarding_children_held: "Safeguarding", safeguarding_children_expiry: "Safeguarding expiry", intro_first_aid_held: "First aid", intro_first_aid_expiry: "First aid expiry" };
const fmtV = v => v === true ? "Held" : v === false ? "Not held" : String(v);

// Upload the FA qualification export, match coaches by FAN number, preview, commit.
export function QualificationImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const reset = () => { setFile(null); setPreview(null); if (ref.current) ref.current.value = ""; };
  const send = async (path, f = file) => {
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };
  const doPreview = async (f) => {
    setFile(f); setPreview(null); setBusy(true);
    try { const d = await send("/admin/coaches/qualifications-import-preview", f); setPreview(d); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't read that file"); reset(); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const d = await send("/admin/coaches/qualifications-import-commit");
      toast.success(`Updated ${d.updated} coach${d.updated === 1 ? "" : "es"} from the FA export`);
      onDone?.(); reset(); onClose?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="qual-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">Import FA qualifications</DialogTitle>
          <DialogDescription className="text-xs opacity-70">Upload the CSV exported from FA (Whole Game System / Learning). Coaches are matched by <strong>FAN number</strong>; columns for qualification, DBS, Safeguarding and First Aid (status and/or expiry) are detected automatically. Nothing changes until you confirm.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block card-tactical rounded-md p-5 text-center cursor-pointer hover:border-[#83B68B]/60" data-testid="qual-import-drop">
            <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" data-testid="qual-import-file" onChange={e => e.target.files?.[0] && doPreview(e.target.files[0])} />
            <FileSpreadsheet className="w-6 h-6 mx-auto text-[#83B68B]" />
            <div className="text-sm mt-2">{file ? file.name : "Choose the FA export CSV"}</div>
            <div className="text-[11px] opacity-50 mt-1">{busy ? "Reading…" : "Click to select a file"}</div>
          </label>

          {preview && (
            <div className="space-y-3" data-testid="qual-import-preview">
              <div className="flex flex-wrap gap-4 text-xs">
                <span><strong className="text-[#83B68B] text-lg font-display">{preview.will_update}</strong> to update</span>
                <span><strong className="text-lg font-display opacity-70">{preview.unchanged}</strong> already up to date</span>
                <span><strong className="text-amber-300 text-lg font-display">{preview.unmatched.length}</strong> not matched</span>
                <span className="opacity-50 ml-auto">Columns: {Object.keys(preview.columns_detected).join(", ")}</span>
              </div>
              {preview.matched.filter(m => m.changes && Object.keys(m.changes).length).length > 0 && (
                <div className="rounded-md border border-border divide-y divide-border max-h-64 overflow-y-auto" data-testid="qual-import-changes">
                  {preview.matched.filter(m => Object.keys(m.changes).length).map(m => (
                    <div key={m.user_id} className="p-3 text-xs flex flex-wrap gap-2 items-start" data-testid={`qual-import-row-${m.user_id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#83B68B] mt-0.5" />
                      <div className="font-semibold">{m.coach_name} <span className="opacity-50 font-normal">FAN {m.fan}</span>{m.name_mismatch && <span className="text-amber-300 font-normal ml-2">CSV says "{m.csv_name}" — check it's the same person</span>}</div>
                      <div className="w-full pl-5 flex flex-wrap gap-1.5">{Object.entries(m.changes).map(([k, v]) => <span key={k} className="px-2 py-0.5 rounded border border-border">{LABELS[k] || k}: <strong>{fmtV(v)}</strong></span>)}</div>
                    </div>
                  ))}
                </div>
              )}
              {preview.unmatched.length > 0 && (
                <details className="text-xs" data-testid="qual-import-unmatched">
                  <summary className="cursor-pointer text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {preview.unmatched.length} row{preview.unmatched.length === 1 ? "" : "s"} couldn't be matched</summary>
                  <ul className="mt-2 space-y-1 opacity-80 pl-4 list-disc">{preview.unmatched.map(u => <li key={u.row}>Row {u.row}: {u.name || "—"} (FAN {u.fan || "—"}) — {u.reason}</li>)}</ul>
                  <div className="opacity-60 mt-2">Add the FAN number to the coach's profile (pencil on their card) and re-upload to match them.</div>
                </details>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { reset(); onClose?.(); }} className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase">Cancel</button>
                <button type="button" onClick={commit} disabled={busy || !preview.will_update} data-testid="qual-import-commit" className="btn-volt px-5 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40 inline-flex items-center gap-1.5"><Upload className="w-3 h-3" /> {busy ? "Importing…" : `Update ${preview.will_update} coach${preview.will_update === 1 ? "" : "es"}`}</button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
